import React, { useEffect, useState, useMemo } from "react";
import Slider from "react-slick";
import { Link, scroller } from "react-scroll";
import api from "../utils/api";
import Spinner from "./Spinner";
import SpinnerSmall from "../components/SpinnerSmall";
import dict from "../utils/dictionary";

const formatPrice = (price) => String(price).replace(".", ",");

const VARIANT_PRODUCT_TYPES = ["champion", "skins", "rank", "unranked"];

const VARIANT_LABELS = {
  champion: {
    "0-20": "0-20 Campeões",
    "20-50": "20-50 Campeões",
    "50-100": "50-100 Campeões",
    "100-165": "100-165 Campeões",
  },
  skins: {
    "0-20": "0-20 Skins",
    "20-50": "20-50 Skins",
    "50-100": "50-100 Skins",
    "100-200": "100-200 Skins",
    "200-300": "200-300 Skins",
    "300-500": "300-500 Skins",
  },
  rank: {
    hierro: "Ferro",
    bronce: "Bronze",
    plata: "Prata",
    oro: "Ouro",
    platino: "Platina",
    esmeralda: "Esmeralda",
    diamante: "Diamante",
    master: "Master",
    grandmaster: "Grandmaster",
    challenger: "Challenger",
  },
  unranked: {
    "40k": "40k BE",
    "50k": "50k BE",
    "60k": "60k BE",
    "70k": "70k BE",
  },
};

const RARITY_COLORS = {
  DEFAULT: "#8e8e8e",
  RARE: "#1e90ff",
  EPIC: "#b146c2",
  LEGENDARY: "#e8872a",
  ULTIMATE: "#e44040",
  MYTHIC: "#ee5a9c",
};

const RARITY_LABELS = {
  DEFAULT: "Default",
  RARE: "Rare",
  EPIC: "Epic",
  LEGENDARY: "Legendary",
  ULTIMATE: "Ultimate",
  MYTHIC: "Mythic",
};

const getVariantLabel = (productType, variant) => {
  const labels = VARIANT_LABELS[productType];
  if (labels && labels[variant.value]) return labels[variant.value];
  return variant.label || variant.value || "?";
};

function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const Price = () => {
  const dictionary = dict()["price"];
  const [products, setProducts] = useState({});
  const [productsLoading, setProductsLoading] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState({
    applied: false,
    discount: 0,
    price: 0,
  });
  const [error, setError] = useState("");
  const [generatingPaymentLoading, setGeneratingPaymentLoading] = useState(false);
  const [region, setRegion] = useState("br");
  const [accessType, setAccessType] = useState("fa");
  const [email, setEmail] = useState(localStorage.getItem("email") ? localStorage.getItem("email") : "");
  const [quantity, setQuantity] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [generatePaymentButtonText, setGeneratePaymentButtonText] = useState({ canBuy: true, text: dictionary["continue"] });
  const [isFirstPart, setIsFirstPart] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [mercadoPagoAvailable, setMercadoPagoAvailable] = useState(false);
  const [stockCounts, setStockCounts] = useState({});

  // Skin Shard Selector State
  const [skinsData, setSkinsData] = useState(null);
  const [availableSkinShards, setAvailableSkinShards] = useState({});
  const [rarityPrices, setRarityPrices] = useState({});
  const [skinShardSearch, setSkinShardSearch] = useState("");
  const [skinShardTooltip, setSkinShardTooltip] = useState(false);
  const [selectedSkinShard, setSelectedSkinShard] = useState(null);
  const [skinShardsLoading, setSkinShardsLoading] = useState(false);
  const [showSkinSelector, setShowSkinSelector] = useState(false);
  const [skinSearchFocused, setSkinSearchFocused] = useState(false);
  const [baseUnitPrice, setBaseUnitPrice] = useState(0);

  const isVariantType = (type) => VARIANT_PRODUCT_TYPES.includes(type);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [productsRes, stockRes, skinsRes] = await Promise.all([api.get("/api/v1/products/active"), api.get("/api/v1/stock/counts"), api.get("/skins.json")]);
        if (productsRes.data.success) {
          const grouped = {};
          productsRes.data.data
            .filter((p) => p.type !== "skin-unranked")
            .forEach((p) => {
              if (!grouped[p.region]) grouped[p.region] = [];
              grouped[p.region].push({ ...p, originalPrice: p.price, link: "#" });
            });
          setProducts(grouped);
          const regions = Object.keys(grouped);
          if (regions.length > 0 && !grouped[region]) {
            setRegion(regions[0]);
          }
        }
        if (stockRes.data.success) {
          setStockCounts(stockRes.data.data);
        }
        if (skinsRes.data && skinsRes.data[0]) {
          setSkinsData(skinsRes.data[0]);
        }
      } catch (err) {
        console.error("Failed to fetch products", err);
      }
      setProductsLoading(false);
    };
    fetchProducts();
  }, []);

  const getLowestVariantPrice = (product) => {
    if (isVariantType(product.type) && product.variants && product.variants.length > 0) {
      const prices = product.variants.filter((v) => v.price > 0).map((v) => v.price);
      return prices.length > 0 ? Math.min(...prices) : product.price;
    }
    return product.price;
  };

  const onChangeEmail = (event) => {
    setEmail(event.target.value.trim());
    if (!validateEmail(event.target.value.trim())) {
      setGeneratePaymentButtonText({ canBuy: false, text: dictionary["writeValidEmail"] });
    } else {
      setGeneratePaymentButtonText({ canBuy: true, text: dictionary["generatePayment"] });
    }
  };
  const onClickRegion = (region) => {
    setRegion(region);
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedSkinShard(null);
    setSkinShardSearch("");
    setShowSkinSelector(false);
  };
  const onClickAccessType = (type) => {
    setAccessType(type);
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedSkinShard(null);
    setSkinShardSearch("");
    setShowSkinSelector(false);
    scroller.scrollTo("product-slider", { smooth: true, duration: 500, offset: -80 });
  };
  const onChangeCoupon = (event) => {
    setCoupon(event.target.value.toUpperCase());
  };
  const onClickCheckCoupon = () => {
    if (coupon) {
      checkCouponCode();
    }
  };
  const getStock = async (product, variantValue, skinShardId) => {
    setIsLoading(true);
    try {
      let headers = {
        "Content-Type": "application/json",
      };
      let payload = { ...product, price: quantity * product.originalPrice };
      if (variantValue) payload.variantValue = variantValue;
      if (skinShardId) payload.selectedSkinShardId = skinShardId;
      let stockRequest = await api.post(`/api/v1/stock/normal/check`, payload, headers);
      let stockOutcome = stockRequest.data.msgCode;
      if (stockOutcome === "PRODUCT_OUT_OF_STOCK" || stockOutcome === "PRODUCT_NOT_FOUND") {
        setGeneratePaymentButtonText({ canBuy: false, text: dictionary["noStock"] });
      }
      if (stockOutcome === "PRODUCT_IN_STOCK") {
        setGeneratePaymentButtonText({ canBuy: true, text: dictionary["continue"] });
      }
    } catch (error) {
      setGeneratePaymentButtonText({ canBuy: false, text: dictionary["errorTryAgain"] });
    }
    setIsLoading(false);
  };
  const generatePreferenceMercadoPago = async () => {
    try {
      setGeneratingPaymentLoading(true);
      setError("");

      let headers = {
        "Content-Type": "application/json",
      };

      let payload = { ...selectedProduct, coupon: discountedPrice.applied ? coupon : "", quantity, email, price: quantity * selectedProduct.originalPrice };
      if (selectedVariant) payload.variantValue = selectedVariant.value;
      if (selectedSkinShard) payload.selectedSkinShardId = selectedSkinShard.skinId;

      let stockRequest = await api.post(`/api/v1/stock/normal/buy/mercadopago`, payload, headers);
      let stockOutcome = stockRequest.data;

      if (stockOutcome.msgCode === "PRODUCT_OUT_OF_STOCK") {
        setError(dictionary["errorNoStock"]);
      }
      if (stockOutcome.msgCode === "PAYMENT_CREATED") {
        var script = document.createElement("script");

        script.src = `https://www.mercadopago.com.${dictionary["countryMpagoScript"]}/integrations/v1/web-payment-checkout.js`;
        script.type = "text/javascript";
        script.dataset.preferenceId = stockOutcome.data.transactionId;
        document.getElementById("mpago-checkout").innerHTML = "";
        document.querySelector("#mpago-checkout").appendChild(script);
        setMercadoPagoAvailable(true);
      }
    } catch (error) {
      setError(dictionary["errorPleaseTryAgain"]);
    }
    setGeneratingPaymentLoading(false);
  };
  const checkCouponCode = async () => {
    try {
      let headers = {
        "Content-Type": "application/json",
      };

      let couponRequest = await api.post(`/api/v1/stock/coupon/check`, { coupon, quantity, price: selectedProduct.price, product: selectedProduct }, headers);
      let couponOutcome = couponRequest.data;

      if (couponOutcome.msgCode === "INVALID_COUPON") {
        setError(dictionary["errorCouponInvalidForQuantity"]);
      }
      if (couponOutcome.msgCode === "INVALID_COUPON_EXPIRED") {
        setError(dictionary["errorCouponExpired"]);
      }
      if (couponOutcome.msgCode === "INVALID_COUPON_NOT_FOUND") {
        setError(dictionary["errorCouponDontExist"]);
      }
      if (couponOutcome.msgCode === "VALID_COUPON") {
        setSelectedProduct({ ...selectedProduct, price: couponOutcome.data.price, discount: couponOutcome.data.discount });
        setDiscountedPrice({ price: couponOutcome.data.price, discount: couponOutcome.data.discount, applied: true });
        setError("");
      }
    } catch (error) {
      setError(dictionary["errorPleaseTryAgain"]);
    }
  };

  const fetchAvailableSkinShards = async (idVerbose, variantValue) => {
    setSkinShardsLoading(true);
    setShowSkinSelector(true);
    try {
      let payload = { idVerbose };
      if (variantValue) payload.variantValue = variantValue;
      const res = await api.post("/api/v1/stock/skin-shards/available", payload);
      if (res.data.success) {
        setAvailableSkinShards(res.data.data);
        if (res.data.rarityPrices) setRarityPrices(res.data.rarityPrices);
      } else {
        setAvailableSkinShards({});
      }
    } catch (err) {
      setAvailableSkinShards({});
    }
    setSkinShardsLoading(false);
  };

  const onClickProduct = async (product) => {
    if (!selectedProduct || product.idVerbose != selectedProduct.idVerbose) {
      setCoupon("");
      setDiscountedPrice({ discount: 0, price: 0, applied: false });
      setError("");
      setSelectedVariant(null);
      setSelectedSkinShard(null);
      setSkinShardSearch("");
      setShowSkinSelector(false);

      setIsFirstPart(true);
      setMercadoPagoAvailable(false);

      setBaseUnitPrice(product.originalPrice);
      setSelectedProduct({ ...product, price: quantity * product.originalPrice });
      getStock({ ...product, quantity: Number(quantity), price: quantity * product.originalPrice });
      fetchAvailableSkinShards(product.idVerbose);
    }
  };

  const onClickProductVariant = (product, variant) => {
    setCoupon("");
    setDiscountedPrice({ discount: 0, price: 0, applied: false });
    setError("");
    setIsFirstPart(true);
    setMercadoPagoAvailable(false);
    setSelectedVariant(variant);
    setSelectedSkinShard(null);
    setSkinShardSearch("");
    setBaseUnitPrice(variant.price);

    const updatedProduct = { ...product, originalPrice: variant.price, price: quantity * variant.price };
    setSelectedProduct(updatedProduct);
    getStock({ ...updatedProduct, quantity: Number(quantity) }, variant.value);
    fetchAvailableSkinShards(product.idVerbose, variant.value);

    scroller.scrollTo("payment", { smooth: true, duration: 500, offset: -80 });
  };

  const onClickVariant = (variant) => {
    setCoupon("");
    setDiscountedPrice({ discount: 0, price: 0, applied: false });
    setError("");
    setIsFirstPart(true);
    setMercadoPagoAvailable(false);
    setSelectedVariant(variant);
    setSelectedSkinShard(null);
    setSkinShardSearch("");
    setBaseUnitPrice(variant.price);

    const updatedProduct = { ...selectedProduct, originalPrice: variant.price, price: quantity * variant.price };
    setSelectedProduct(updatedProduct);
    getStock({ ...updatedProduct, quantity: Number(quantity) }, variant.value);
    fetchAvailableSkinShards(selectedProduct.idVerbose, variant.value);
  };

  const onChangeQuantity = (event) => {
    if ((Number.isInteger(Number(event.target.value)) || event.target.value === "") && event.target.value.length <= 2) {
      setDiscountedPrice({ discount: 0, price: 0, applied: false });
      setError("");
      setIsFirstPart(true);
      setMercadoPagoAvailable(false);

      setQuantity(event.target.value);
      setSelectedProduct({ ...selectedProduct, quantity: Number(event.target.value), price: event.target.value * selectedProduct.originalPrice });
      getStock({ ...selectedProduct, quantity: Number(event.target.value), price: event.target.value * selectedProduct.originalPrice }, selectedVariant ? selectedVariant.value : undefined, selectedSkinShard ? selectedSkinShard.skinId : undefined);
    }
  };
  const onClickContinue = () => {
    setIsFirstPart(false);
    if (!validateEmail(email)) {
      setGeneratePaymentButtonText({ canBuy: false, text: dictionary["writeValidEmail"] });
    } else {
      setGeneratePaymentButtonText({ canBuy: true, text: dictionary["generatePayment"] });
    }
  };

  const onClickGeneratePaymentMercadoPago = () => {
    localStorage.setItem("email", email);
    generatePreferenceMercadoPago();
  };

  // Build flat list of all skins from skins.json for the skin shard selector
  const allSkins = useMemo(() => {
    if (!skinsData) return [];
    const flat = [];
    Object.entries(skinsData).forEach(([championName, skins]) => {
      skins.forEach((skin) => {
        flat.push({ ...skin, championName });
      });
    });
    return flat;
  }, [skinsData]);

  // Available skins (shown when focused, no search)
  const availableSkinsList = useMemo(() => {
    return allSkins.filter((s) => !!availableSkinShards[s.skinId]);
  }, [allSkins, availableSkinShards]);

  // Filter skins based on search (search all skins, not just available)
  const filteredSkins = useMemo(() => {
    if (!skinShardSearch.trim()) return availableSkinsList;
    const search = skinShardSearch.trim().toLowerCase();
    return allSkins.filter((s) => s.skinName.toLowerCase().includes(search) || s.championName.toLowerCase().includes(search)).slice(0, 50);
  }, [allSkins, availableSkinsList, skinShardSearch]);

  // Get the base unit price (variant or product price, without skin shard extra)
  const getBaseUnitPrice = () => baseUnitPrice;

  const getSkinShardExtraPrice = (skin) => {
    if (!skin || !skin.rarity) return 0;
    return rarityPrices[skin.rarity] || 0;
  };

  const onSelectSkinShard = (skin) => {
    setSelectedSkinShard(skin);
    setSkinShardSearch("");
    setIsFirstPart(true);
    setMercadoPagoAvailable(false);
    setCoupon("");
    setDiscountedPrice({ discount: 0, price: 0, applied: false });
    setError("");

    const extra = getSkinShardExtraPrice(skin);
    const basePrice = getBaseUnitPrice();
    const newOriginalPrice = basePrice + extra;
    const updatedProduct = { ...selectedProduct, originalPrice: newOriginalPrice, price: quantity * newOriginalPrice };
    setSelectedProduct(updatedProduct);

    getStock(
      { ...updatedProduct, quantity: Number(quantity) },
      selectedVariant ? selectedVariant.value : undefined,
      skin.skinId
    );
  };

  const onClearSkinShard = () => {
    setSelectedSkinShard(null);
    setSkinShardSearch("");
    setIsFirstPart(true);
    setMercadoPagoAvailable(false);
    setCoupon("");
    setDiscountedPrice({ discount: 0, price: 0, applied: false });
    setError("");

    const basePrice = getBaseUnitPrice();
    const updatedProduct = { ...selectedProduct, originalPrice: basePrice, price: quantity * basePrice };
    setSelectedProduct(updatedProduct);

    getStock(
      { ...updatedProduct, quantity: Number(quantity) },
      selectedVariant ? selectedVariant.value : undefined
    );
  };

  const options = {
    dots: false,
    arrows: true,
    accessibility: false,
    infinite: false,
    margin: 30,
    speed: 300,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const regionProducts = (products[region] || []).filter((p) => p.idVerbose.startsWith(accessType + "_"));
  let DataList = regionProducts.map((val, i) => {
    const hasVariantPricing = isVariantType(val.type) && val.variants && val.variants.length > 0;
    const displayPrice = hasVariantPricing ? getLowestVariantPrice(val) : val.price;
    const productStock = stockCounts[val._id] || { total: 0, variants: {} };
    const productInStock = hasVariantPricing ? val.variants.some((v) => (productStock.variants[v.value] || 0) > 0) : productStock.total > 0;
    return (
      <div className={`price-item d-flex ${selectedProduct && selectedProduct.idVerbose === val.idVerbose ? "price-selected" : ""} `} key={val.idVerbose}>
        <div className="price-block" style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/assets/images/price-back.webp")`, opacity: productInStock ? 1 : 0.6 }}>
          <div className="price-type">
            <img style={{ width: "170px", height: "170px" }} alt="product-image" src={`${process.env.PUBLIC_URL}/assets/images/${val.idVerbose}.webp`}></img>
          </div>
          <div className="mrp">
            <div style={{ fontSize: "16px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em", margin: "25px 25px" }} className="user-type">
              {val.name}
            </div>
            <div className="price-devide"></div>
            <h2 style={selectedProduct && selectedProduct.idVerbose === val.id ? { color: "#448cda" } : {}}>
              {dictionary["localCurrencySymbol"]}
              {formatPrice(displayPrice)}
              {hasVariantPricing && "+"}
            </h2>
            <div style={{ color: "#777777", fontWeight: "700", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.1em" }} className="price-year">
              {dictionary["localCurrency"]}
            </div>
            <div className="price-devide"></div>
          </div>
          <ul className="price-feature">
            {(val.description || []).map((descItem, i) => (
              <li key={descItem}>
                <i className="fas fa-check mr-2" style={{ color: "#568a56" }}></i>
                <span style={{ color: "#353535" }}>{descItem}</span>
              </li>
            ))}
          </ul>
          {hasVariantPricing ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "center", padding: "0 15px", marginBottom: "15px" }}>
              {val.variants.map((v) => {
                const variantStock = productStock.variants[v.value] || 0;
                const isInStock = variantStock > 0;
                const isSelected = selectedVariant && selectedVariant._id === v._id && selectedProduct && selectedProduct.idVerbose === val.idVerbose;
                return (
                  <div
                    key={v._id}
                    onClick={isInStock ? () => onClickProductVariant(val, v) : undefined}
                    style={{
                      padding: "6px 14px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: "700",
                      background: !isInStock ? "#ccc" : isSelected ? "#e12447" : "rgba(255,255,255,0.9)",
                      color: !isInStock ? "#888" : isSelected ? "#fff" : "#e12447",
                      border: !isInStock ? "2px solid #aaa" : "2px solid #e12447",
                      cursor: isInStock ? "pointer" : "not-allowed",
                      transition: "all 0.2s",
                      userSelect: "none",
                      textAlign: "center",
                      lineHeight: "1.3",
                    }}
                  >
                    <div>{getVariantLabel(val.type, v)}</div>
                    <div style={{ fontSize: "10px", fontWeight: "400", opacity: 0.8 }}>{isInStock ? `${dictionary["localCurrencySymbol"]}${formatPrice(v.price)}` : dictionary["noStock"] || "Sin stock"}</div>
                  </div>
                );
              })}
            </div>
          ) : productInStock ? (
            <Link href="/#payment" activeClass="active" to={"payment"} spy={true} smooth={true} offset={-80} duration={500} onClick={() => onClickProduct(val)}>
              <div className="btn btn-custom" style={{ backgroundColor: "#e12447" }}>
                <span>{dictionary["chooseButton"]}</span>
              </div>
            </Link>
          ) : (
            <div className="btn btn-custom" style={{ backgroundColor: "#888", cursor: "not-allowed" }}>
              <span>{dictionary["noStock"] || "Sin stock"}</span>
            </div>
          )}
        </div>
      </div>
    );
  });

  // Determine if we should show the payment/quantity sections
  const isVariantProduct = selectedProduct && isVariantType(selectedProduct.type) && selectedProduct.variants && selectedProduct.variants.length > 0;
  const showPaymentSections = !isVariantProduct || selectedVariant;

  return (
    <>
      <div className="skins-title">
        <div className="container">
          <div className="row">
            <div className="col-md-12 col-sm-12 col-text-center d-align-center">
              <h2 className="title" style={{ justifyContent: "center", display: "flex", color: "#dadada" }}>
                <span>{dictionary["accountsTitle"]}</span>
              </h2>
            </div>
          </div>
        </div>
      </div>
      <section id="price" className="price">
        <div className="container" style={{ textAlign: "center", paddingBottom: "30px" }}>
          <h2 style={{ fontSize: "3rem", lineHeight: "40px" }}>{dictionary["chooseYourRegion"]}</h2>
        </div>
        <div className="container" style={{ display: "flex", justifyContent: "space-evenly", marginBottom: "30px", userSelect: "none" }}>
          {Object.keys(products).map((r) => (
            <div key={r} style={{ cursor: "pointer", backgroundImage: `url("${process.env.PUBLIC_URL}/assets/images/${r}.webp")` }} className={`region-item col-3 ${region === r ? "region-selected" : ""}`} onClick={() => onClickRegion(r)}>
              <span>{dictionary["regions"][r] || r.toUpperCase()}</span>
            </div>
          ))}
        </div>
        <div className="container" style={{ display: "flex", justifyContent: "center", gap: "20px", marginBottom: "50px", userSelect: "none" }}>
          <div
            onClick={() => onClickAccessType("fa")}
            style={{
              cursor: "pointer",
              padding: "12px 40px",
              borderRadius: "8px",
              fontWeight: "700",
              fontSize: "16px",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              border: accessType === "fa" ? "2px solid #e12447" : "2px solid #444",
              background: accessType === "fa" ? "#e12447" : "transparent",
              color: accessType === "fa" ? "#fff" : "#aaa",
              transition: "all 0.2s",
            }}
          >
            FA <span style={{ fontSize: "11px", fontWeight: "400", opacity: 0.8 }}>(ACESSO COMPLETO)</span>
          </div>
          <div
            onClick={() => onClickAccessType("nfa")}
            style={{
              cursor: "pointer",
              padding: "12px 40px",
              borderRadius: "8px",
              fontWeight: "700",
              fontSize: "16px",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              border: accessType === "nfa" ? "2px solid #e12447" : "2px solid #444",
              background: accessType === "nfa" ? "#e12447" : "transparent",
              color: accessType === "nfa" ? "#fff" : "#aaa",
              transition: "all 0.2s",
            }}
          >
            NFA <span style={{ fontSize: "11px", fontWeight: "400", opacity: 0.8 }}>(SEM ACESSO COMPLETO)</span>
          </div>
        </div>
        <div style={{ textAlign: "center", marginBottom: "30px", marginTop: "-35px" }}>
          <p style={{ fontSize: "12px", color: "#888", margin: 0 }}>
            <strong style={{ color: "#aaa" }}>NFA:</strong> Você não pode fazer alterações. &nbsp;|&nbsp;
            <strong style={{ color: "#aaa" }}>FA:</strong> Você pode alterar a conta.
          </p>
        </div>
        <div id="product-slider" className="container-fluid" style={{ maxWidth: "1400px" }}>
          <div className="row">
            <div className="col-sm-12">
              {productsLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                  <Spinner />
                </div>
              ) : regionProducts.length === 0 ? (
                <p style={{ textAlign: "center", color: "#aaa", padding: "40px" }}>No products available for this region.</p>
              ) : (
                <Slider className="price-carousel owl-carousel owl-theme slick-margin" {...options}>
                  {" "}
                  {DataList}
                </Slider>
              )}
            </div>
          </div>
        </div>
        <div id="payment" className="container" style={{ marginTop: "50px" }}>
          {selectedProduct && (
            <div className="row" style={{ display: "flex", justifyContent: "center" }}>
              {selectedProduct && (
                <div className="innerpage-decor d-none d-xl-block">
                  <div className="innerpage-faq1" style={{ bottom: "1px", right: "1px", top: "auto" }}>
                    <img src={`${process.env.PUBLIC_URL}/assets/images/money.webp`} alt="poro with essence" />
                  </div>
                  <div className="innerpage-faq2">
                    <img src={`${process.env.PUBLIC_URL}/assets/images/gold.webp`} alt="gold" />
                  </div>
                </div>
              )}
              {isLoading ? (
                <div style={{ height: "475px", alignItems: "center", display: "flex" }}>
                  <Spinner />
                </div>
              ) : (
                <div className="payment-skin" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <h1 style={{ lineHeight: "40px" }}>{dictionary["makeYourPayment"]}</h1>
                  <h2 style={{ margin: "0", fontSize: "20px", textAlign: "center" }}>{selectedProduct.name}</h2>
                  <div className="skins-description-payment">
                    {(selectedProduct.description || []).map((desc) => (
                      <div key={desc}>
                        <i className="fas fa-check mr-2" style={{ color: "#568a56" }}></i>
                        <span style={{ color: "#b7b7b7" }}>{desc}</span>
                      </div>
                    ))}
                  </div>

                  {/* Variant Selector */}
                  {isVariantProduct && (
                    <div style={{ marginTop: "20px" }}>
                      <h2 style={{ margin: "0 0 10px 0", fontSize: "16px", textAlign: "center" }}>{dictionary["selectVariant"] || "Selecciona una opcion:"}</h2>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                        {selectedProduct.variants.map((v) => (
                          <div
                            key={v._id}
                            onClick={() => onClickVariant(v)}
                            style={{
                              padding: "10px 18px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: selectedVariant && selectedVariant._id === v._id ? "2px solid #e12447" : "2px solid #444",
                              background: selectedVariant && selectedVariant._id === v._id ? "#e1244722" : "#1a1a2e",
                              color: selectedVariant && selectedVariant._id === v._id ? "#fff" : "#ccc",
                              textAlign: "center",
                              transition: "all 0.2s",
                              userSelect: "none",
                            }}
                          >
                            <div style={{ fontWeight: "700", fontSize: "14px" }}>{getVariantLabel(selectedProduct.type, v)}</div>
                            <div style={{ fontSize: "13px", color: "#aaa" }}>
                              {dictionary["localCurrencySymbol"]}
                              {formatPrice(v.price)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skin Shard Selector */}
                  {showPaymentSections && showSkinSelector && (
                    <div style={{ marginTop: "20px" }}>
                      <h2 style={{ margin: "0 0 14px 0", fontSize: "20px", fontWeight: "700", textAlign: "center", color: "#e12447", textTransform: "uppercase", letterSpacing: "1px", padding: "10px 0", borderTop: "2px solid #e1244733", borderBottom: "2px solid #e1244733", position: "relative" }}>
                        {dictionary["chooseSkinShard"] || "Escolha uma skin (opcional):"}
                        <span
                          onClick={(e) => { e.stopPropagation(); setSkinShardTooltip(!skinShardTooltip); }}
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", marginLeft: "8px", width: "22px", height: "22px", borderRadius: "50%", border: "2px solid #e12447", color: "#e12447", fontSize: "13px", fontWeight: "bold", cursor: "pointer", verticalAlign: "middle", position: "relative" }}
                        >
                          ?
                          {skinShardTooltip && (
                            <span style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: "#1a1a2e", border: "1px solid #e12447", borderRadius: "8px", padding: "10px 14px", fontSize: "13px", fontWeight: "400", color: "#fff", width: "280px", textTransform: "none", letterSpacing: "0", lineHeight: "1.5", zIndex: 100, textAlign: "left", whiteSpace: "normal" }}>
                              A conta conterá o fragmento de skin escolhido. Pode ou não ter Essência Laranja suficiente para converter em skin permanente.
                            </span>
                          )}
                        </span>
                      </h2>
                      {selectedSkinShard ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", padding: "12px", background: "#1a1a2e", borderRadius: "8px", border: "2px solid #e12447" }}>
                          <img
                            src={`/skins/${selectedSkinShard.skinId}.jpg`}
                            alt={selectedSkinShard.skinName}
                            style={{ width: "60px", height: "60px", borderRadius: "8px", objectFit: "cover" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                          <div>
                            <div style={{ fontWeight: "700", fontSize: "14px", color: "#fff" }}>{selectedSkinShard.skinName}</div>
                            <div style={{ fontSize: "12px", color: "#aaa" }}>{selectedSkinShard.championName}</div>
                            <div style={{ fontSize: "11px", fontWeight: "700", color: RARITY_COLORS[selectedSkinShard.rarity] || "#aaa" }}>
                              {RARITY_LABELS[selectedSkinShard.rarity] || selectedSkinShard.rarity}
                              {getSkinShardExtraPrice(selectedSkinShard) > 0 && (
                                <span style={{ color: "#ccc", fontWeight: "400" }}> (+{dictionary["localCurrencySymbol"]}{formatPrice(getSkinShardExtraPrice(selectedSkinShard))})</span>
                              )}
                            </div>
                          </div>
                          <div
                            onClick={onClearSkinShard}
                            style={{ cursor: "pointer", padding: "4px 12px", borderRadius: "6px", background: "#e12447", color: "#fff", fontSize: "12px", fontWeight: "700" }}
                          >
                            X
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            value={skinShardSearch}
                            onChange={(e) => setSkinShardSearch(e.target.value)}
                            onFocus={() => setSkinSearchFocused(true)}
                            onBlur={() => setTimeout(() => setSkinSearchFocused(false), 200)}
                            placeholder={dictionary["searchSkinPlaceholder"] || "Buscar por campeão ou skin..."}
                            style={{ width: "100%", marginBottom: "8px" }}
                          />
                          {skinShardsLoading ? (
                            <div style={{ textAlign: "center", padding: "12px" }}><SpinnerSmall /></div>
                          ) : (skinSearchFocused || skinShardSearch.trim()) && (
                            <div style={{ maxHeight: "300px", overflowY: "auto", background: "#1a1a2e", borderRadius: "8px", border: "1px solid #333" }}>
                              {filteredSkins.length === 0 ? (
                                <div style={{ padding: "16px", textAlign: "center", color: "#888" }}>
                                  {dictionary["noSkinsFound"] || "Nenhuma skin encontrada"}
                                </div>
                              ) : (
                                filteredSkins.map((skin) => {
                                  const isAvailable = !!availableSkinShards[skin.skinId];
                                  return (
                                    <div
                                      key={skin.skinId}
                                      onClick={isAvailable ? () => onSelectSkinShard(skin) : undefined}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #2a2a3e",
                                        cursor: isAvailable ? "pointer" : "not-allowed",
                                        opacity: isAvailable ? 1 : 0.4,
                                        transition: "background 0.15s",
                                        background: "transparent",
                                      }}
                                      onMouseEnter={(e) => { if (isAvailable) e.currentTarget.style.background = "#2a2a4e"; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                    >
                                      <img
                                        src={`/skins/${skin.skinId}.jpg`}
                                        alt={skin.skinName}
                                        style={{ width: "44px", height: "44px", borderRadius: "6px", objectFit: "cover" }}
                                        onError={(e) => { e.target.src = `${process.env.PUBLIC_URL}/assets/images/price-back.webp`; }}
                                      />
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: "600", fontSize: "13px", color: isAvailable ? "#fff" : "#666" }}>{skin.skinName}</div>
                                        <div style={{ fontSize: "11px", color: isAvailable ? "#aaa" : "#555" }}>{skin.championName}</div>
                                        <div style={{ fontSize: "10px", fontWeight: "700", color: isAvailable ? (RARITY_COLORS[skin.rarity] || "#aaa") : "#555" }}>
                                          {RARITY_LABELS[skin.rarity] || skin.rarity}
                                        </div>
                                      </div>
                                      <div style={{ textAlign: "right" }}>
                                        <div style={{ fontSize: "11px", fontWeight: "700", color: isAvailable ? "#27ae60" : "#888" }}>
                                          {isAvailable ? (dictionary["available"] || "Disponível") : (dictionary["unavailable"] || "Indisponível")}
                                        </div>
                                        {isAvailable && (rarityPrices[skin.rarity] || 0) > 0 && (
                                          <div style={{ fontSize: "10px", color: "#e8872a" }}>
                                            +{dictionary["localCurrencySymbol"]}{formatPrice(rarityPrices[skin.rarity])}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {showPaymentSections && (
                    <>
                      <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between" }}>
                        <div style={{}}>
                          <h2 style={{ margin: "0px", fontSize: "20px" }}>
                            {dictionary["price"]}:{" "}
                            <span style={discountedPrice.applied ? { textDecoration: "line-through" } : {}}>
                              {dictionary["localCurrencySymbol"]}
                              {formatPrice(selectedProduct.originalPrice * quantity)}
                            </span>{" "}
                            {discountedPrice.applied ? (
                              <>
                                <span>
                                  {dictionary["localCurrencySymbol"]}
                                  {formatPrice(discountedPrice.price)}
                                </span>
                                <span style={{ fontSize: "12px" }}> (-{discountedPrice.discount}%)</span>
                              </>
                            ) : (
                              <span></span>
                            )}
                          </h2>
                        </div>
                        <div style={{ display: "flex" }}>
                          <h2 style={{ fontSize: "10px", marginRight: "10px" }}>{dictionary["quantity"]}</h2>
                          <input disabled={mercadoPagoAvailable ? true : false} onChange={onChangeQuantity} value={quantity} style={{ height: "min-content", width: "30px" }}></input>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", marginTop: "20px" }}>
                        <h2 style={{ fontSize: "20px", padding: "0px 20px 0px 0px", margin: "0px" }}>{dictionary["coupon"]}</h2>
                        <input maxLength={16} value={coupon} placeholder={dictionary["useCouponToGetDiscount"]} onChange={onChangeCoupon} style={{ height: "min-content", width: "100%" }}></input>
                        <div onClick={onClickCheckCoupon} style={{ marginLeft: "20px", height: "min-content" }} className={`btn btn-custom theme-color ${mercadoPagoAvailable ? "btn-disabled" : ""}`}>
                          {dictionary["use"]}
                        </div>
                      </div>
                      {isFirstPart ? (
                        <div style={{ width: "100%" }} className={`btn btn-custom theme-color mt-3 ${!generatePaymentButtonText.canBuy ? "btn-disabled" : ""}`} onClick={() => onClickContinue()}>
                          {generatePaymentButtonText.text}
                        </div>
                      ) : (
                        <>
                          {!mercadoPagoAvailable && !generatingPaymentLoading && (
                            <>
                              <input placeholder={dictionary["yourEmail"]} onChange={onChangeEmail} value={email}></input>
                              <p style={{ margin: "5px 0px 5px 0px" }}>Estou ciente que e um produto/servico digital e estou de acordo com os termos do site.</p>
                              <div style={{ width: "100%" }} className={`btn btn-custom theme-color mt-3 ${!validateEmail(email) ? "btn-disabled" : ""}`} onClick={() => onClickGeneratePaymentMercadoPago()}>
                                {generatePaymentButtonText.text}
                              </div>
                            </>
                          )}
                          <div id="mpago-checkout" style={{ display: "flex", justifyContent: "center" }}></div>
                          {generatingPaymentLoading && (
                            <div style={{ textAlign: "center", marginTop: "20px" }}>
                              <SpinnerSmall />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {error && (
                    <div className="error-message">
                      <i style={{ color: "#d42121" }} className="fas fa-times mr-2"></i>
                      <span>{error}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Price;
